import { computed, onScopeDispose, watch, type Ref } from 'vue'

import { useDocumentVisibility, useOnline } from '@vueuse/core'

/**
 * Calls `tick` every `intervalMs` while `active` is true, the tab is in front and the browser is online.
 * It pauses while the tab is hidden or offline, and on return ticks at once if a tick fell due meanwhile.
 * It stops the moment `active` turns false or the owning component unmounts, and never doubles a tick
 * still in flight.
 */
export function usePollWhile(active: Ref<boolean>, tick: () => Promise<unknown>, intervalMs: number) {
  const visibility = useDocumentVisibility()
  const online = useOnline()
  let timer: ReturnType<typeof setInterval> | null = null
  let inFlight = false
  let lastTickAt = Date.now()

  const runTick = async () => {
    if (inFlight) return
    inFlight = true
    lastTickAt = Date.now()
    try {
      await tick()
    } finally {
      inFlight = false
    }
  }

  const stop = () => {
    if (timer) clearInterval(timer)
    timer = null
  }

  const start = (catchUp: boolean) => {
    stop()
    if (catchUp && Date.now() - lastTickAt >= intervalMs) void runTick()
    timer = setInterval(runTick, intervalMs)
  }

  const inFront = computed(() => visibility.value !== 'hidden' && online.value)

  watch(
    [active, inFront],
    ([isActive, isInFront], previous) => {
      if (!isActive || !isInFront) return stop()
      const returned = previous?.[0] === true && previous[1] === false
      if (!returned) lastTickAt = Date.now()
      start(returned)
    },
    { immediate: true }
  )
  onScopeDispose(stop)

  return { stop }
}
